// Copy button for a standalone code block in a body that arrives as HTML: the
// portal, the widget, and the review diff. A Tiptap editor draws its own
// button through the node view, and a code group copies from its header, so
// both are left alone here.

export const CODE_COPY_ICON =
	'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2.25 6.75V13.25C2.25 14.355 3.145 15.25 4.25 15.25H11.75"/><path d="M7.25 12.25H13.75C14.8546 12.25 15.75 11.355 15.75 10.25V4.75C15.75 3.645 14.8546 2.75 13.75 2.75H7.25C6.1454 2.75 5.25 3.645 5.25 4.75V10.25C5.25 11.355 6.1454 12.25 7.25 12.25Z"/></svg>'
export const CODE_CHECK_ICON =
	'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.25 9.75l3.5 3.5 7.5-7.5"/></svg>'

export interface CodeCopyLabels {
	copy: string
	copied: string
}

const DEFAULT_LABELS: CodeCopyLabels = { copy: "Copy code", copied: "Copied" }

const COPY_SELECTOR = "[data-code-copy]"
const BOUND_ATTRIBUTE = "data-code-copy-bound"
const COPIED_ATTRIBUTE = "data-code-copied"

const BUTTON_CLASS =
	"absolute right-2 top-2 z-10 flex items-center gap-1 rounded-lg bg-white px-1.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm ring-1 ring-black/5 dark:bg-gray-800 dark:text-gray-200 dark:ring-white/10"

// The reader borrows the `.ProseMirror` class for typography, so the class
// alone says nothing; only an editable surface carries `contenteditable`.
export function isInsideEditor(node: HTMLElement): boolean {
	return Boolean(node.closest("[contenteditable]"))
}

const isInCodeGroup = (pre: HTMLElement): boolean =>
	Boolean(
		pre.closest('[data-panel-group="code-group"], [data-type="code-group"]'),
	)

const codeTextOf = (button: HTMLElement): string => {
	const pre = button.closest("pre") ?? button.parentElement
	return pre?.querySelector("code")?.textContent ?? pre?.textContent ?? ""
}

function markCopied(button: HTMLElement, labels: CodeCopyLabels): void {
	if (button.getAttribute(COPIED_ATTRIBUTE) === "true") return
	const original = button.innerHTML
	button.setAttribute(COPIED_ATTRIBUTE, "true")
	button.innerHTML = CODE_CHECK_ICON
	button.setAttribute("aria-label", labels.copied)
	setTimeout(() => {
		button.innerHTML = original
		button.setAttribute("aria-label", labels.copy)
		button.removeAttribute(COPIED_ATTRIBUTE)
	}, 2000)
}

function copyFromButton(button: HTMLElement, labels: CodeCopyLabels): void {
	const text = codeTextOf(button)
	if (!text) return
	void navigator.clipboard
		?.writeText(text)
		.then(() => markCopied(button, labels))
		.catch(() => undefined)
}

function buildButton(doc: Document, labels: CodeCopyLabels): HTMLElement {
	const button = doc.createElement("button")
	button.type = "button"
	button.className = BUTTON_CLASS
	button.setAttribute("data-code-copy", "")
	button.setAttribute("aria-label", labels.copy)
	button.innerHTML = CODE_COPY_ICON
	return button
}

// A body written through the v2 API carries a bare `<pre>`, so the button is
// added here rather than trusted to the stored markup.
export function ensureCodeCopyButtons(
	container: HTMLElement,
	labels: CodeCopyLabels = DEFAULT_LABELS,
): void {
	container.querySelectorAll<HTMLElement>("pre").forEach((pre) => {
		if (isInsideEditor(pre) || isInCodeGroup(pre)) return

		// Older bodies carry an inline handler calling a global the reader bundles
		// never defined, which is what left the button dead.
		pre.removeAttribute("onmouseenter")
		pre
			.querySelectorAll<HTMLElement>(COPY_SELECTOR)
			.forEach((button) => button.removeAttribute("onclick"))

		if (!pre.querySelector("code")) return
		pre.classList.add("relative")
		if (pre.querySelector(COPY_SELECTOR)) return
		pre.appendChild(buildButton(pre.ownerDocument, labels))
	})
}

// For a container whose clicks are already delegated through React.
export function handleCodeCopyClick(
	target: HTMLElement,
	labels: CodeCopyLabels = DEFAULT_LABELS,
): boolean {
	const button = target.closest<HTMLElement>(COPY_SELECTOR)
	if (!button || isInsideEditor(button)) return false
	copyFromButton(button, labels)
	return true
}

export function enhanceCodeCopy(
	container: HTMLElement,
	labels: CodeCopyLabels = DEFAULT_LABELS,
): () => void {
	ensureCodeCopyButtons(container, labels)

	if (container.getAttribute(BOUND_ATTRIBUTE) === "true") return () => undefined
	container.setAttribute(BOUND_ATTRIBUTE, "true")

	const onClick = (event: Event) => {
		const target = event.target as HTMLElement
		const button = target.closest<HTMLElement>(COPY_SELECTOR)
		if (!button || isInsideEditor(button)) return
		event.preventDefault()
		copyFromButton(button, labels)
	}

	container.addEventListener("click", onClick)
	return () => {
		container.removeAttribute(BOUND_ATTRIBUTE)
		container.removeEventListener("click", onClick)
	}
}
